import { AfterViewInit, Component, computed, effect, OnDestroy, OnInit, signal } from "@angular/core";
import { MatIconRegistry } from "@angular/material/icon";
import { DomSanitizer } from "@angular/platform-browser";
import { ActivatedRoute, NavigationEnd, Router } from "@angular/router";
import { TranslateService } from "@ngx-translate/core";
import { AuthService, PlatformService, UserService } from "@evc/platform";
import { WebComponentsConfigService } from "@evc/web-components";
import { LiveAssetNavigationService, AssetTypeNavigationService, RoleService } from "@rvi/asset-modules";
import { of, map, from, Subject, Subscription } from "rxjs";
import { concatMap, filter, mergeMap, takeUntil, tap } from "rxjs/operators";
import { User } from "./_objects/user";
import { OfflineSetupService } from "./_offline/services/offline-setup.service";
import { STANDALONE } from "./_offline/standalone-data/standalone-data";
import { StandaloneMigrationOrchestrator } from "./_services/asset-module/migration/standalone-migration-orchestrator.service";
import { SendOfflineDataService } from "./_services/sync/send-offline-data.service";
import { AppInfoDbService } from "./_services/database/app-info-db.service";
import { AppBootstrapService } from "./_services/app-bootstrap.service";
import { LoggedInUserDbService } from "./_services/database/logged-in-user-db.service";
import { LoginUserOnlineService } from "./_services/http-api/login-user-online.service";
import { MeUserService } from "./_services/http-api/me-user.service";
import { LangUpdateService } from "./_services/lang-update.service";
import { UsersStateService } from "./_services/user-state/users-state.service";
import { RouteAssetModule, RoutePage } from "./app-routes.enum";
import { LANGUAGE, LOCAL_STORAGE, SUPPORTED_LANGUAGES, TENANT_ROLE } from "./app.constants";
import { ElectronService } from "./electron.service";
import { toSignal } from "@angular/core/rxjs-interop";
import { ApplicationMode, NetworkStatusService } from "./_services/network-status.service";
import { EvcModalService } from "./_shared/services/modal/evc-modal.service";
import { MigrationProgressModalComponent } from "./_services/asset-module/migration/migration-progress-modal/migration-progress-modal.component";

const ASSET_MODULE_ROUTES = new Set(
  Object.values(RouteAssetModule).map(path => `/${path}`)
);

@Component({
  selector: "app-root",
  templateUrl: "./app.component.html",
  styleUrls: ["./app.component.scss"],
  providers: [],
  standalone: false
})
class AppComponent implements OnInit, OnDestroy, AfterViewInit {
  public appReady = signal(false);
  public isNotAgreedTerms = false;
  public defaultLanguage: string = LANGUAGE.ENGLISH;
  public isDirectAccess: boolean = false;
  public platformReady = this.plaformService.ready;
  public isFullView = computed(() => this.assetTypeNavigation.isViewerPage() || this.liveAssetNavigation.isViewerPage());

  private routerSub = new Subscription();
  private onDestroy$ = new Subject();
  private appMode = toSignal(this.networkStatusService.applicationMode$);

  constructor(
    private appInfoDbService: AppInfoDbService,
    private appBootstrapService: AppBootstrapService,
    private electronService: ElectronService,
    private langUpdateService: LangUpdateService,
    private loginUserService: LoginUserOnlineService,
    private meUserService: MeUserService,
    private networkStatusService: NetworkStatusService,
    private offlineSetupService: OfflineSetupService,
    private route: ActivatedRoute,
    private router: Router,
    private translate: TranslateService,
    private usersService: UsersStateService,
    private componentConfigService: WebComponentsConfigService,
    private assetTypeNavigation: AssetTypeNavigationService,
    private liveAssetNavigation: LiveAssetNavigationService,
    private assetModuleRoleService: RoleService,
    private userService: UserService,
    private plaformService: PlatformService,
    private migrationService: StandaloneMigrationOrchestrator,
    private loggedInUserDbService: LoggedInUserDbService,
    private sendOfflineDataService: SendOfflineDataService,
    private modalService: EvcModalService,
    private authService: AuthService,
    iconRegistry: MatIconRegistry,
    sanitizer: DomSanitizer
  ) {
    iconRegistry.addSvgIconSet(sanitizer.bypassSecurityTrustResourceUrl("./assets/sprite/sprite.svg"));

    iconRegistry.addSvgIconSet(sanitizer.bypassSecurityTrustResourceUrl("./assets/images/osc_lib_icons.svg"));
    this.componentConfigService.set("ASSETS_BASE_PATH", "./assets/");

    this.isDirectAccess = document.URL.includes("?imageId=");

    effect(() => {
      const applicationMode = this.appMode();

      if (applicationMode) {
        this.setAssetModuleRole(applicationMode);
      }
    });
  }

  get isDesktop() {
    return this.networkStatusService.isDesktop();
  }

  get isBrianApp() {
    return this.networkStatusService.isBrianMode;
  }

  async ngOnInit() {
    this.lockAppOrientation();
    this.setLanguage();
    this.usersService.isFirstAccess$.pipe(takeUntil(this.onDestroy$)).subscribe((isFirst) => (this.isNotAgreedTerms = isFirst));
    this.showAssetModuleNotification();
  }

  ngAfterViewInit() {
    this.initAfterViewInit();
  }

  private async initAuth(): Promise<void> {
    if (this.authService.isOffline()) {
      return;
    }

    try {
      await this.authService.init();
    } catch (error) {
      console.error("[AppComponent] Authentication service initialization failed:", error);
      this.authService.isOffline.set(true);
      await this.networkStatusService.switchOfflineMode();
    }
  }

  private showAssetModuleNotification() {
      this.router.events
        .pipe(
          filter(event => event instanceof NavigationEnd)
        )
        .subscribe((event: NavigationEnd) => {
          const route = event.urlAfterRedirects.split('?')[0];

          if (!ASSET_MODULE_ROUTES.has(route)) {
            return;
          }

          const key = `first-visit-asset-module`;
          const firstVisitTimestamp = localStorage.getItem(key);

          if (!firstVisitTimestamp) {
            const modalRef = this.modalService.showModal(MigrationProgressModalComponent);
            modalRef.componentRef?.instance.isCompleted.set(true);
          }
        });
  }

  private setAssetModuleRole(applicationMode: ApplicationMode) {
    if (NetworkStatusService.isVisolOffline(applicationMode)) {
      return this.assetModuleRoleService.setRole("User");
    }

    const profile = this.userService.profile();
    if (!profile?.roles?.length) return;

    const role = RoleService.parseRole(profile.roles[0].name);
    this.assetModuleRoleService.setRole(role);
  }

  private async initAfterViewInit() {
    await this.initAuth();

    if (this.isDesktop) {
      await this.appInfoDbService.putEnvironment().toPromise();
      await this.appInfoDbService.putAppLaunchType().toPromise();
      await this.offlineSetupService.setupDesktopApp(this.route.queryParams, this.onDestroy$);

      if (this.networkStatusService.isOffline) {
        this.migrationService.migrate().subscribe({
          next: (state) => {
            if (state === "Completed") {
              this.goBackToStandalone();
            }
          }
        });
      }
    }

    this.meUserService
      .getMe()
      .pipe(
        tap((user) => this.networkStatusService.setIsOnPremise(user.id === STANDALONE.USER_ID)),
        concatMap((user) => from(this.setMe(user)).pipe(map(() => user))),
        concatMap((user) => (user.isAgreedTerms ? this.loadPostAgreementState() : of(null)))
      )
      .subscribe({
        complete: () => {
          this.appReady.set(true);
          if (this.isDesktop && this.networkStatusService.isOnline) {
            this.sendOfflineDataService.syncData().pipe(takeUntil(this.onDestroy$)).subscribe();
          }
        }
      });

    this.loginUserSubscribes();
  }

  /**
   * If a user attempted to sync on-premise data to the cloud using a version that predates asset-module support, revert them to standalone mode to prevent data loss.
   */
  private goBackToStandalone() {
    return this.meUserService
      .getMe()
      .pipe(
        mergeMap((me) => {
          const isStandaloneUser = me.id == STANDALONE.USER_ID;
          if (!isStandaloneUser) {
            return this.loggedInUserDbService.delete(me.id);
          }
          return of({});
        })
      )
      .subscribe(async () => {
        this.networkStatusService.setIsOnPremise(true);
        this.router.navigate(["/"]);
      });
  }

  private loadPostAgreementState() {
    return this.appBootstrapService.runPostAgreementBootstrap();
  }

  ngOnDestroy(): void {
    this.onDestroy$.next(undefined);
    this.onDestroy$.unsubscribe();
    this.routerSub.unsubscribe();
  }

  async setMe(me: User) {
    await this.approveUser(me);

    // In standalone mode, UserService.profile() is not populated by the platform so we set it here based on tenantAttributes.
    if (this.networkStatusService.isOnPremise) {
      const roleName = TENANT_ROLE.OWNER;
      this.userService.setUser({
        id: me.id,
        displayName: `${me.givenName} ${me.surname}`,
        givenName: me.givenName,
        surname: me.surname,
        preferredLanguage: LANGUAGE.ENGLISH,
        roles: [{ id: me.tenantAttributes?.[0]?.id ?? "", name: roleName, isReadOnly: false }]
      });
    }

    this.isNotAgreedTerms = me && !me.isAgreedTerms;
    this.usersService.setIsFirstAccess(this.isNotAgreedTerms);
  }

  public async setLanguage() {
    this.translate.addLangs(SUPPORTED_LANGUAGES.map((lang) => lang.code));

    let resolvedLanguage = this.defaultLanguage;
    const storedLanguage = localStorage.getItem(LOCAL_STORAGE.LANGUAGE_PREFERENCE);

    if (storedLanguage) {
      this.translate.use(storedLanguage);
      this.langUpdateService.updateLanguage(storedLanguage);
      return;
    }

    if (this.isDesktop) {
      const osLanguage = await this.electronService.getOsLanguage();

      const supportedLanguages = this.translate.getLangs();

      resolvedLanguage =
        supportedLanguages.find((language) => language === osLanguage) ??
        supportedLanguages.find((language) => language.startsWith(osLanguage.split("-")[0])) ??
        this.defaultLanguage;
    }

    this.langUpdateService.prepare(this.translate, this.onDestroy$);

    this.langUpdateService.updateLanguage(resolvedLanguage);
  }

  loginUserSubscribes() {
    this.loginUserService.onLoginUserWrapper$.pipe(takeUntil(this.onDestroy$)).subscribe();
  }

  private async approveUser(me: User) {
    if (this.isDesktop) {
      await this.offlineSetupService.approveUserOffline(me);
    } else {
      this.loginUserService.loginUser = me;
      await this.loginUserService.setTenantAndUserProfile();
    }
  }

  private async lockAppOrientation() {
    try {
      if (this.isBrianApp && window.screen.orientation["lock"]) {
        console.debug("locking device orientation to landscape");
        await window.screen.orientation["lock"]("landscape");
      }
    } catch (error: any) {
      console.warn("Error locking device orientation:", error.message);
    }
  }
}

export default AppComponent;
